import { format } from 'date-fns'
import { formatISO9075 } from 'date-fns'
import { startCase } from 'lodash'
import { getInLocalTime } from '../../../../util/timeHelpers'
import { ContentClassificationView, ContentView, EnrichedContent } from '../../../../api/client'
import { getSocialMediaContentLink } from '../ContentLink'

type FormatMetadataOptions = {
    content: ContentView
    enrichedContent: EnrichedContent | undefined
    moderation?: string | null
    classifications?: ContentClassificationView[]
}

/**
 * Converts a string to camelCase and removes non-alphanumeric characters.
 * "My Facebook Page" → "myFacebookPage"
 * "John's Account!" → "johnsAccount"
 */
function toCamelCase(str: string | null | undefined): string {
    if (!str) return 'unknown'

    return str
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
        .split(/\s+/) // Split on whitespace
        .filter(Boolean) // Remove empty strings
        .map((word, index) => {
            const lower = word.toLowerCase()
            if (index === 0) return lower
            return lower.charAt(0).toUpperCase() + lower.slice(1)
        })
        .join('')
}

/**
 * Formats a classification for text display.
 */
function formatClassification(classification: ContentClassificationView): string {
    const classifierName = startCase(classification.classifierName)

    if (classification.classification !== null && classification.classification !== undefined) {
        const percentage = (classification.classification * 100).toFixed(0)
        return `${classifierName}: ${percentage}%`
    }
    return classifierName
}

/**
 * Generates a descriptive filename for the metadata download.
 * Format: commentPostedDate_teamName_accountName_commenterName_contentId.txt
 */
function generateFilename(options: FormatMetadataOptions): string {
    const { content } = options

    // Format date as YYYYMMDD
    const datePosted = content.datePosted
        ? format(getInLocalTime(content.datePosted), 'yyyyMMdd')
        : 'noDate'

    const team = toCamelCase(content.account?.teamName)
    const account = toCamelCase(content.account?.accountName || content.account?.serviceAccountUsername)
    const commenter = toCamelCase(content.serviceAuthorName || content.serviceAuthorDisplayUsername)
    const contentId = content.serviceContentId ?? content.id ?? 'noId'

    return `${datePosted}_${team}_${account}_${commenter}_${contentId}.txt`
}

/**
 * Formats content metadata as plain text for download.
 */
export function formatMetadataAsText(options: FormatMetadataOptions): string {
    const { content, enrichedContent, moderation, classifications } = options
    const datePosted = content.datePosted
        ? formatISO9075(getInLocalTime(content.datePosted))
        : 'Not set'
    const socialMediaContentLink =
        content.socialMediaContentLink || getSocialMediaContentLink(enrichedContent, content)

    const notSet = 'Not set'

    // Format moderation status
    const moderationStatus = moderation
        ? moderation.charAt(0) + moderation.slice(1).toLowerCase()
        : 'Not set'

    // Format classifications
    const classificationLines: string[] = []
    if (classifications && classifications.length > 0) {
        classifications.forEach((classification) => {
            classificationLines.push(formatClassification(classification))
        })
    } else {
        classificationLines.push('None')
    }

    const lines = [
        'Content Metadata',
        '',
        'Author Details',
        '',
        `Platform account ID: ${content.serviceAuthorId ?? notSet}`,
        `Username: ${content.serviceAuthorName ?? notSet}`,
        `Display name: ${content.serviceAuthorDisplayUsername ?? notSet}`,
        `Profile URL: ${content.socialMediaAuthorLink ?? notSet}`,
        `Arwen profile ID: ${content.authorProfileId ?? notSet}`,
        '',
        'Content Details',
        '',
        `Platform content ID: ${content.serviceContentId ?? notSet}`,
        `Content text: ${content.content ?? notSet}`,
        `Posted at: ${datePosted}`,
        `Content URL: ${socialMediaContentLink ?? notSet}`,
        `Arwen content ID: ${content.id ?? notSet}`,
        '',
        'Original Post Details',
        '',
        `Post ID: ${content.originalPostId ?? notSet}`,
        `Post URL: ${content.socialMediaPostLink ?? notSet}`,
        '',
        'Moderation',
        '',
        `Status: ${moderationStatus}`,
        `Classifications: ${classificationLines.length === 1 ? classificationLines[0] : ''}`,
        ...(classificationLines.length > 1 ? classificationLines.map((line) => `  - ${line}`) : []),
        ''
    ]

    return lines.join('\n')
}

/**
 * Downloads the metadata as a plain text file.
 */
export function downloadMetadataAsText(options: FormatMetadataOptions): void {
    const text = formatMetadataAsText(options)
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)

    const filename = generateFilename(options)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}
